import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Megaphone, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface Banner {
  id: string;
  message: string;
  link_url: string | null;
  link_label: string | null;
  starts_at: string;
  ends_at: string;
}

const DISMISS_KEY = 'bmg-banner-dismissed';
const MAX_TIMER_MS = 6 * 60 * 60 * 1000;

export default function SiteBanner({ hidden }: { hidden?: boolean }) {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('site_banners')
        .select('id, message, link_url, link_label, starts_at, ends_at')
        .gt('ends_at', new Date().toISOString())
        .order('starts_at', { ascending: true })
        .limit(5);
      if (!cancelled && !error && Array.isArray(data)) setBanners(data as Banner[]);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const boundaries = banners
      .flatMap((b) => [Date.parse(b.starts_at), Date.parse(b.ends_at)])
      .filter((t) => t > now);
    if (boundaries.length === 0) return;
    const wait = Math.min(Math.min(...boundaries) - now + 50, MAX_TIMER_MS);
    const t = setTimeout(() => setNow(Date.now()), wait);
    return () => clearTimeout(t);
  }, [banners, now]);

  const active = banners.find((b) => Date.parse(b.starts_at) <= now && Date.parse(b.ends_at) > now);
  if (!active || hidden || dismissed === active.id) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, active.id);
    setDismissed(active.id);
  };

  const linkClass = 'inline-flex items-center gap-1 font-semibold underline underline-offset-2 decoration-bmg-ink/40 hover:decoration-bmg-ink transition-colors whitespace-nowrap';
  const isExternal = active.link_url?.startsWith('https://');

  return (
    <div className="absolute inset-x-0 top-full animate-fade-in" role="region" aria-label="Beskjed">
      <div className="bg-bmg-yellow text-bmg-ink shadow-md">
        <div className="container-bmg flex items-center gap-3 py-2 text-sm">
          <Megaphone className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <p className="flex-1 min-w-0 leading-snug">
            <span className="font-medium">{active.message}</span>
            {active.link_url && active.link_label && (
              <>
                {' '}
                {isExternal ? (
                  <a href={active.link_url} target="_blank" rel="noopener noreferrer" className={linkClass}>
                    {active.link_label} <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <Link to={active.link_url} className={linkClass}>
                    {active.link_label} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </>
            )}
          </p>
          <button
            type="button"
            onClick={dismiss}
            className="p-1 -mr-1 rounded hover:bg-bmg-yellow-dark/40 transition-colors"
            aria-label="Lukk beskjed"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
